import { type ReactElement } from "react";
import { Outlet, useLocation, useMatch } from "react-router-dom";
import { ArrowUpRight, BookOpen, Bot, Sparkles } from "lucide-react";
import {
  SectionHeader,
  SettingsDetailsButton,
  SettingsMetaCard,
  SettingsPageLayout,
  TabPanel,
  Tabs,
} from "@playbookai/ui";
import { getDocumentationUrl } from "../../config/runtime.js";
import {
  SETTINGS_CONNECTIONS_ROUTE,
  SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE,
  SETTINGS_PLAYER_AI_ROUTE,
} from "../../routes.js";
import { useGuardedNavigate } from "../../settings/leave-guard.js";

import { useConnectionTools } from "./use-connection-tools.js";

const PLAYER_AI_TABS = [
  { value: "general", label: "General" },
  { value: "connected-apps", label: "Connected apps" },
] as const;

/* Player AI settings host (PB-5134, d16): the header, the meta card and the
 * sub-tabs. General lives at the section URL and Connected apps at its own,
 * so a link from a connection can land on the right tab; the tabs navigate
 * through the leave guard, so a dirty General draft asks before it is lost. */
export function PlayerAISettings(): ReactElement {
  const location = useLocation();
  const connectionId = useMatch(
    `${SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE}/:connectionId`,
  )?.params.connectionId;
  const navigate = useGuardedNavigate();
  const documentationUrl = getDocumentationUrl();
  const tab = location.pathname.startsWith(
    SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE,
  )
    ? "connected-apps"
    : "general";

  const meta = connectionId ? (
    <ConnectionAccessAside connectionId={connectionId} />
  ) : (
    <SettingsMetaCard
      icon={Sparkles}
      title={
        tab === "general"
          ? "About Player AI"
          : location.pathname === SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE
            ? "About connected apps"
            : "Connection access"
      }
      description={
        tab === "general"
          ? "Set your personal communication preferences and choose a default profile for new conversations. SOLVE follows its facilitator’s tone."
          : "Tool access is specific to each connection. Player AI can use only tools permitted by you and your workspace."
      }
      links={[
        ...(tab === "connected-apps"
          ? [
              {
                label: "Manage your connections",
                href: SETTINGS_CONNECTIONS_ROUTE,
              },
            ]
          : []),
        ...(documentationUrl
          ? [
              {
                label: "Assistant & AI docs",
                icon: BookOpen,
                href: documentationUrl,
                external: true,
                accessory: <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />,
              },
            ]
          : []),
      ]}
    />
  );

  return (
    <div className="pb-motion-content-enter">
      <SectionHeader
        variant="settings"
        leading={<Bot className="w-4 h-4 text-pb-cyan-text" />}
        title="Player AI"
        description="Set how Player AI responds and choose which connected apps it may use."
        actionsSlot={<SettingsDetailsButton meta={meta} />}
      />
      <SettingsPageLayout mainWidth="fluid" meta={meta}>
        <Tabs
          aria-label="Player AI settings"
          tone="ai"
          items={[...PLAYER_AI_TABS]}
          value={tab}
          onValueChange={(value) =>
            navigate(
              value === "connected-apps"
                ? SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE
                : SETTINGS_PLAYER_AI_ROUTE,
            )
          }
        >
          <TabPanel value={tab} className="pt-4.5">
            <Outlet />
          </TabPanel>
        </Tabs>
      </SettingsPageLayout>
    </div>
  );
}

function ConnectionAccessAside({ connectionId }: { connectionId: string }) {
  const { query } = useConnectionTools(connectionId);
  const tools = query.data ?? [];
  return (
    <SettingsMetaCard
      icon={Sparkles}
      title="Connection access"
      description={`${query.isSuccess ? `${tools.filter((t) => t.playerAiInEffect).length} of ${tools.length} tools allowed. ` : ""}Permissions apply only to this connection. Workspace restrictions still apply.`}
      links={[
        { label: "Connection settings", href: SETTINGS_CONNECTIONS_ROUTE },
      ]}
    />
  );
}
